<template>
  <div class="row">
    <div class="page-title">
      <div class="row">
        <div class="col-sm-6 col-12">
          <h2>Wallets</h2>
        </div>
        <div class="col-sm-6 col-12 text-end">
          <a class="btn btn-outline-primary" href="/admin/wallets/approvals">
            <i class="fa-solid fa-check-double"></i> Approval Queue
          </a>
        </div>
      </div>
    </div>

    <div class="col-xl-12 xl-100 box-col-6">
      <div class="card">
        <div class="card-body social-list filter-cards-view">
          <div class="row mb-3">
            <div class="col-md-6">
              <input type="search" class="form-control" placeholder="Search by rider name, email, phone or wallet id (min 3 characters)"
                     v-model="query" @input="onSearchInput">
            </div>
            <div class="col-md-3">
              <select class="form-select" v-model="status" @change="() => load(1)">
                <option :value="null">All statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="FROZEN">Frozen</option>
                <option value="SUSPENDED">Suspended</option>
                <option value="CLOSED">Closed</option>
              </select>
            </div>
          </div>

          <div v-if="isLoading" class="text-center py-4">
            <span class="spinner-border spinner-border-sm me-2"></span> Loading...
          </div>
          <div v-else-if="rows.length === 0" class="text-muted text-center py-4">No wallets match this search.</div>
          <div v-else class="table-responsive">
            <table class="table table-borderless">
              <thead>
              <tr>
                <th>Rider</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Available</th>
                <th>Total</th>
                <th>Provisioned</th>
                <th></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in rows" :key="row.walletId">
                <td>{{ row.riderDisplayName || 'N/A' }}</td>
                <td>{{ row.maskedEmail || '' }}</td>
                <td>{{ row.maskedPhone || '' }}</td>
                <td v-html="walletStatus(row.status)"></td>
                <td v-html="minorCurrency(row.availableBalanceMinor)"></td>
                <td v-html="minorCurrency(row.totalBalanceMinor)"></td>
                <td>{{ formatDate(row.provisionedOn) }}</td>
                <td>
                  <a class="btn btn-sm btn-outline-primary" :href="`/admin/wallets/${row.walletId}`">
                    <i class="fas fa-eye"></i> View
                  </a>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <nav v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-2">
            <button class="btn btn-outline-secondary btn-sm" :disabled="page <= 1" @click="load(page - 1)">Previous</button>
            <span class="text-muted small">Page {{ page }} of {{ totalPages }} ({{ totalRows }} wallets)</span>
            <button class="btn btn-outline-secondary btn-sm" :disabled="!hasNext" @click="load(page + 1)">Next</button>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import _ from 'lodash';
import { minorCurrency, walletStatus } from '../utility/DatatableRenderer';

const query = ref('');
const status = ref(null);
const rows = ref([]);
const page = ref(1);
const totalPages = ref(0);
const totalRows = ref(0);
const hasNext = ref(false);
const isLoading = ref(false);

const formatDate = (value) => value ? new Date(value).toLocaleDateString() : '';

// Server-searched, server-paged (the plan's deliberate departure from DataTables.net for this
// surface — WalletDao has no unpaged finder, and the list becomes one row per rider at full
// rollout). A query under three characters is sent as blank; the server treats it the same way and
// WalletAdminValidator enforces the floor independently.
const load = async (targetPage) => {
  const trimmed = query.value.trim();
  if (trimmed.length > 0 && trimmed.length < 3) {
    return;
  }
  isLoading.value = true;
  try {
    const params = new URLSearchParams();
    if (trimmed.length >= 3) params.set('q', trimmed);
    if (status.value) params.set('status', status.value);
    params.set('page', String(targetPage));
    const response = await fetch(`/api/admin/wallets?${params.toString()}`);
    if (response.ok) {
      const data = await response.json();
      rows.value = data.items || [];
      page.value = data.page || 1;
      totalPages.value = data.totalPages || 0;
      totalRows.value = data.totalRows || 0;
      hasNext.value = !!data.hasNext;
    }
  } catch (error) {
    console.error('Error loading wallets:', error);
  } finally {
    isLoading.value = false;
  }
};

const onSearchInput = _.debounce(() => load(1), 350);

onMounted(() => load(1));
</script>
